import React, { useCallback, useEffect, useReducer } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { WarningAmber } from '@mui/icons-material';
import DialogContentText from '@mui/material/DialogContentText';
import { useNotification, useRequest } from 'shared/hooks';
import { getBuildingDot1x, updateBuildingDot1x } from 'shared/api/buildings';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import {
  configureGroupDpsks,
  getSmartzone,
  removeBuildingSmartzoneAndDpsks,
  setSmartzone,
} from 'shared/api/wireless';
import {
  checkVsz,
  checkVszZone,
  checkWlan,
  getSmartzoneWlans,
  getVszZones,
  searchVszs,
} from 'shared/api/vsz';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import {
  AsyncSearchableSelect,
  DependentAsyncSearchableSelect,
} from 'shared/components/select/SearchableSelects';
import { dateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import UserPrompt from 'shared/components/UserPrompt';
import { DPSK_TYPES } from 'shared/constants/building';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import { NumberEditor } from 'shared/components/data-display/editors';

const initialSmartzoneData = {
  dpsk_type: DPSK_TYPES.group,
  dpsk_length: 8,
  smartzone_id: null,
};

const initialSmartzoneHandlerData = {
  validators: {
    validWlan: true,
    validZone: true,
    validVsz: false,
    validUpdates: true,
    vszError: false,
  },
  dialogs: {
    promptIsOpen: false,
    errorIsOpen: false,
    saveIsOpen: false,
  },
  displayWlan: null,
  displayZone: null,
  displayVsz: null,
};

function reducer(state, { action, payload }) {
  switch (action) {
    case DataReducerActions.update:
      if (payload.key === 'vsz_id' && payload.value !== state.data.vsz_id && payload.value !== state.updatedData.vsz_id) {
        return {
          ...state,
          dirty: true,
          updatedData: {
            ...state.updatedData,
            vsz_id: payload.value,
            zone_id: undefined,
            wlan_id: undefined,
          },
        };
      }
      if (payload.key === 'zone_id' && payload.value !== state.data.zone_id) {
        return {
          ...state,
          dirty: true,
          updatedData: {
            ...state.updatedData,
            zone_id: payload.value,
            wlan_id: undefined,
          },
        };
      }
      return dataReducer(state, { action, payload });
    case DataReducerActions.set:
      return {
        ...state,
        dirty: false,
        updatedData: {},
        data: { ...payload },
      };
    case DataReducerActions.reload:
      return {
        dirty: false,
        updatedData: {},
        data: initialSmartzoneData,
        ...initialSmartzoneHandlerData,
      };
    case 'validator':
      return {
        ...state,
        validators: {
          ...state.validators,
          [payload.key]: payload.value,
        },
      };
    case 'dialogs':
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          [payload.key]: payload.value,
        },
      };
    case 'displayVsz':
      return {
        ...state,
        displayVsz: payload,
      };
    case 'displayZone':
      return {
        ...state,
        displayZone: payload,
      };
    case 'displayWlan':
      return {
        ...state,
        displayWlan: payload,
      };
    default:
      return dataReducer(state, { action, payload });
  }
}

const smartzoneDescriptionsUpper = [
  {
    key: 'dpsk_type',
    label: 'DPSK Type',
    editor: withStaticSelect({
      disableClearable: true,
      options: [DPSK_TYPES.group],
      valueAsLabel: true,
    }),
  },
];

const smartzoneDescriptionsLower = [
  {
    key: 'dpsk_length',
    label: 'DPSK Length',
    editor: NumberEditor,
  },
  {
    key: 'updated_at',
    label: 'Updated At',
    readOnly: true,
    formatter: (sz) => dateString(sz.updated_at),
  },
];

/**
 * @param {Object} props
 * @param {number} props.buildingId - Building ID
 * @param {Array} props.buildingUnits - Array of building units
 * @param {number} props.updateCount - Update counter
 * @param {Function} props.triggerUpdate - Function to trigger update
 */
export default function ZoneInfo({
  buildingId,
  buildingUnits,
  updateCount,
  triggerUpdate,
}) {
  const { isLoading, request } = useRequest();
  const notification = useNotification();

  const [state, dispatch] = useReducer(reducer, {
    ...initialSmartzoneHandlerData,
    ...initialDataReducerState,
    data: { ...initialSmartzoneData },
  });

  const {
    data,
    dirty,
    updatedData,
    validators,
    dialogs,
    displayVsz,
    displayZone,
    displayWlan,
  } = state;

  const {
    validVsz,
    validZone,
    validWlan,
    validUpdates,
    vszError,
  } = validators;

  const {
    promptIsOpen,
    errorIsOpen,
    saveIsOpen,
  } = dialogs;

  const {
    id,
    vsz_id: vszId,
    wlan_id: wlanId,
    wlan_ssid: wlanSsid,
    zone_id: zoneId,
    zone_name: zoneName,
    smartzone_id: smartzoneId,
  } = data;

  const {
    vsz_id: newVszId,
    zone_id: newZoneId,
    zone_name: newZoneName,
  } = updatedData;

  const updateValidator = (key, value) => dispatch({
    action: 'validator',
    payload: { key, value },
  });

  const updateDialogs = (key, value) => dispatch({
    action: 'dialogs',
    payload: { key, value },
  });

  const updateDisplayVsz = (value) => {
    if (value?.id === undefined) {
      dispatch({
        action: 'displayVsz',
        payload: null,
      });
    } else {
      dispatch({
        action: 'displayVsz',
        payload: value,
      });
    }
  };

  const updateDisplayZone = (value) => {
    if (value?.id === undefined) {
      dispatch({
        action: 'displayZone',
        payload: null,
      });
    } else {
      dispatch({
        action: 'displayZone',
        payload: value,
      });
    }
  };

  const updateDisplayWlan = (value) => {
    if (value?.id === undefined) {
      dispatch({
        action: 'displayWlan',
        payload: null,
      });
    } else {
      dispatch({
        action: 'displayWlan',
        payload: value,
      });
    }
  };

  const update = (key, value) => dispatch({
    action: DataReducerActions.update,
    payload: { key, value },
  });

  const setZoneWlan = (zoneVal, wlanVal) => {
    updateDisplayZone(zoneVal);
    updateDisplayWlan(wlanVal);
  };

  const setValidZoneWlan = (zoneVal, wlanVal) => {
    updateValidator('validZone', zoneVal);
    updateValidator('validWlan', wlanVal);
  };

  const updateDot1x = async (body) => {
    if (body) {
      const response = await request(updateBuildingDot1x(buildingId, body));
      if (response?.smartzone_id) {
        triggerUpdate();
      }
    }
  };

  const clearChanges = () => {
    setZoneWlan(
      (zoneId ? { id: zoneId, name: zoneName } : null),
      (wlanId ? { id: wlanId, ssid: wlanSsid } : null),
    );
    updateDisplayVsz(data?.vsz || null);
    updateValidator('vszError', false);
    dispatch({ action: DataReducerActions.clear });
  };

  const updateVsz = (updatedVszName, updatedVszId) => {
    update('vsz_name', updatedVszName);
    update('vsz_id', updatedVszId);
  };

  const updateZone = (updatedZoneName, updatedZoneId) => {
    update('zone_name', updatedZoneName);
    update('zone_id', updatedZoneId);
  };

  const updateWlan = (updatedWlanSsid, updatedWlanId) => {
    update('wlan_ssid', updatedWlanSsid);
    update('wlan_id', updatedWlanId);
  };

  const setBuildingGroupDPSK = async () => {
    const response = await request(configureGroupDpsks(buildingId));
    if (response) {
      notification.pushSuccess(
        `Set Group DPSKs: ${response.new_count} newly created, ${response.updated_count} updated.`,
      );
    }
    return response;
  };

  const saveChanges = async () => {
    if (dirty && validUpdates) {
      await request(
        setSmartzone({ ...data, ...updatedData }),
        'SmartZone Saved Successful',
      ).then((r) => {
        setValidZoneWlan(true, true);
        if (r && r.id !== smartzoneId) {
          updateDot1x({ smartzone_id: r.id });
        } else {
          triggerUpdate();
        }
      });
    }
  };

  const saveChangesSetGroupDPSKs = async () => {
    if (dirty && validUpdates) {
      await request(
        setSmartzone({ ...data, ...updatedData }),
        'SmartZone Saved Successful',
      ).then(async (r) => {
        setValidZoneWlan(true, true);
        if (r && r.id !== smartzoneId) {
          const response = await request(updateBuildingDot1x(buildingId, { smartzone_id: r.id }));
          if (response?.smartzone_id) {
            setBuildingGroupDPSK();
          }
        }
        triggerUpdate();
      });
    }
  };

  const checkVszHandler = async () => {
    const response = await request(checkVsz(newVszId || vszId));
    if (response) {
      updateValidator('validVsz', true);
      updateValidator('vszError', false);
      if (newVszId) {
        setZoneWlan(null, null);
        setValidZoneWlan(true, true);
      }
    } else {
      setValidZoneWlan(false, false);
      updateValidator('validVsz', false);
      updateValidator('vszError', true);
      setZoneWlan({ id: zoneId, name: zoneName }, { id: wlanId, ssid: wlanSsid });
    }
  };

  const checkZoneHandler = async () => {
    const response = await request(checkVszZone(vszId, zoneId));
    if (!response) {
      setValidZoneWlan(false, false);
      setZoneWlan(null);
    } else {
      updateValidator('validZone', true);
      updateDisplayZone({ id: zoneId, name: zoneName });
    }
  };

  const checkWlanHandler = async () => {
    const response = await request(checkWlan(vszId, zoneId, wlanId));
    if (!response) {
      updateValidator('validWlan', false);
      updateDisplayWlan(null);
    } else {
      updateValidator('validWlan', true);
      updateDisplayWlan({ id: wlanId, ssid: wlanSsid });
    }
  };

  useEffect(() => {
    (async () => {
      const dot1xresponse = await request(getBuildingDot1x(buildingId));
      if (dot1xresponse?.smartzone_id) {
        const response = await request(getSmartzone(dot1xresponse.smartzone_id));
        if (response) {
          dispatch({
            action: DataReducerActions.set,
            payload: { ...response, smartzone_id: dot1xresponse.smartzone_id },
          });
        }
      } else {
        dispatch({ action: DataReducerActions.reload });
        updateDisplayWlan(null);
      }
    })();
    // request is a stable useCallback, so listing it does not change run frequency.
  }, [buildingId, updateCount, request]);

  useEffect(() => {
    //  If there is a VSZ change, there must be a zone change
    if (newVszId
        && !newZoneName) {
      updateValidator('validUpdates', false);
      return;
    }
    // If there is a Zone change, there must be a WLAN change
    if (newZoneName
        && !updatedData?.wlan_ssid) {
      updateValidator('validUpdates', false);
      return;
    }
    if (!validUpdates) {
      updateValidator('validUpdates', true);
    }
    // newVszId/newZoneName derive from updatedData; the validUpdates write is guarded
    // and idempotent, so listing these is loop-safe.
  }, [updatedData, newVszId, newZoneName, validUpdates]);

  useEffect(() => {
    if (newVszId || vszId) {
      checkVszHandler();
    }
    // Re-check VSZ only when vszId/newVszId change. checkVszHandler is recreated each
    // render and dispatches the state it reads, so listing it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vszId, newVszId]);

  useEffect(() => {
    if (newVszId !== undefined) {
      setZoneWlan(null, null);
      updateZone(null, null);
      return;
    }
    if (zoneId && validVsz) {
      checkZoneHandler();
    }
    // Reconcile zone only on zoneId/newVszId/validVsz change. The handlers are recreated
    // each render and dispatch the state these deps derive from, so listing them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId, newVszId, validVsz]);

  useEffect(() => {
    if (newVszId !== undefined || newZoneId !== undefined || newZoneName !== undefined) {
      updateDisplayWlan(null);
      updateWlan(null, null);
      updateValidator('validWlan', true);
      return;
    }
    if (wlanId && validVsz && validZone) {
      checkWlanHandler();
    }
    // newVszId/validZone are read in the body (real triggers, loop-safe scalars). The
    // handlers (checkWlanHandler/updateWlan) are recreated each render and dispatch the
    // state these deps derive from, so they are intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wlanId, newVszId, newZoneId, newZoneName, validVsz, validZone]);

  const deleteZoneConfig = async () => {
    const response = await request(removeBuildingSmartzoneAndDpsks(buildingId), 'SmartZone removed');
    if (response) {
      updateDialogs('promptIsOpen', false);
      updateDisplayZone(null);
      updateValidator('vszError', false);
      triggerUpdate();
    }
  };

  const onVszChange = (newVsz) => {
    updateDisplayVsz(newVsz);
    updateVsz(newVsz.ip, newVsz.id);
  };

  const onWlanChange = (newWlan) => {
    updateDisplayWlan(newWlan);
    updateWlan(newWlan.ssid, newWlan.id);
  };

  const onZoneChange = (newZone) => {
    updateDisplayZone(newZone);
    updateZone(newZone.name, newZone.id);
  };

  // Effective VSZ id: the staged value if the VSZ was just changed, else the saved one.
  const effectiveVszId = newVszId !== undefined ? newVszId : vszId;

  // Memoized: a new searchRequest identity re-fires the select's load effect in a loop.
  const getVszWlan = useCallback(
    (dependentZoneId) => getSmartzoneWlans(effectiveVszId, dependentZoneId),
    [effectiveVszId],
  );

  return (
    <>
      <DataFormatter
        data={data}
        descriptions={smartzoneDescriptionsUpper}
        editing
        updateValue={update}
        updatedData={updatedData}
      />
      <Grid
        container
        columnSpacing={1}
        sx={{
          width: '100%',
        }}
      >
        <Grid size={12}>
          <AsyncSearchableSelect
            label="VSZ"
            getOptionLabel={(o) => `${o.ip} (ver ${o.version})`}
            onChange={onVszChange}
            disableClearable
            preloadOptions
            searchRequest={searchVszs}
            selectedOption={displayVsz || data?.vsz}
            placeholder="Select VSZ..."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DependentAsyncSearchableSelect
            label="Zone Name"
            getOptionLabel={(o) => o.name}
            onChange={onZoneChange}
            dependentId={validVsz ? (newVszId || vszId) : null}
            disableClearable
            preloadOptions
            searchRequest={getVszZones}
            selectedOption={displayZone}
            placeholder="Select Zone..."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DependentAsyncSearchableSelect
            label="WLAN"
            getOptionLabel={(o) => o.ssid}
            onChange={onWlanChange}
            disabled={!displayZone?.id || !validVsz}
            dependentId={validVsz ? displayZone?.id : null}
            disableClearable
            preloadOptions
            searchRequest={getVszWlan}
            selectedOption={displayWlan}
            placeholder="Select WLAN..."
          />
        </Grid>
      </Grid>
      <DataFormatter
        data={data}
        descriptions={smartzoneDescriptionsLower}
        editing
        updateValue={update}
        updatedData={updatedData}
      />
      <Grid
        size={12}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          mt: '10px',
          flexWrap: 'wrap-reverse',
          gap: 1,
        }}
      >
        {((vszError || smartzoneId) && (!validWlan || !validZone || !validVsz))
            && (
            <>
              <Tooltip title="Zone configuration issue found">
                <IconButton
                  color={BUTTON_COLORS.error}
                  onClick={() => updateDialogs('errorIsOpen', true)}
                >
                  <WarningAmber />
                </IconButton>
              </Tooltip>
              <Dialog
                open={errorIsOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">Zone configuration issue found</DialogTitle>
                <DialogContent>
                  <DialogContentText component="span" id="alert-dialog-description">
                    <Typography>{`Configuration issues with VSZ "${displayVsz?.ip || data?.vsz?.ip}":`}</Typography>
                    <ul>
                      {!validVsz && (
                      <li>
                        {`VSZ "${displayVsz?.ip || data?.vsz?.ip}" not found`}
                      </li>
                      )}
                      {!validZone && displayZone?.name && (
                      <li>
                        {`Zone "${displayZone?.name}" not found`}
                      </li>
                      )}
                      {!validWlan && displayWlan?.ssid && (
                      <li>
                        {`WLAN "${displayWlan?.ssid}" not found for Zone "${displayZone?.name}"`}
                      </li>
                      )}
                    </ul>
                    <Grid>
                      <Typography sx={{ paddingBottom: '8px' }}>
                        This could be due to removal from the VSZ or connectivity issues:
                      </Typography>
                      <ul>
                        <li>
                          If due to removal from the VSZ, delete or update this configuration
                        </li>
                        <li>
                          If due to connectivity or unexpected issues please contact Devs
                        </li>
                      </ul>
                    </Grid>
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button variant="outlined" onClick={() => updateDialogs('errorIsOpen', false)}>Back</Button>
                </DialogActions>
              </Dialog>
            </>
            )}
        {(dirty || smartzoneId === null) ? (
          <>
            <Button
              variant={VARIANTS.outlined}
              disabled={!dirty}
              onClick={clearChanges}
            >
              Clear
            </Button>
            <Button
              disabled={!(dirty && validUpdates)}
              onClick={() => updateDialogs('saveIsOpen', true)}
              variant={VARIANTS.contained}
            >
              Save
            </Button>
            <Dialog
              open={saveIsOpen}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">Save Smartzone</DialogTitle>
              <DialogContent>
                <DialogContentText component="span" id="alert-dialog-description">
                  <Typography>{`You are attempting to save a new SmartZone for ${buildingId}`}</Typography>
                  This building has the following units:
                  <ul>
                    {buildingUnits.map((unit) => (
                      <li key={unit.id}>
                        {unit.name}
                      </li>
                    ))}
                  </ul>
                  For which a DPSK can be set
                  <Grid>
                    <Typography sx={{ paddingBottom: '8px' }}>
                      Either save and set group DPSK simultaneously or only save the SmartZone
                    </Typography>
                    Group DPSKs may also be set independently after SmartZone is saved.
                  </Grid>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  variant={VARIANTS.outlined}
                  onClick={() => {
                    saveChanges();
                    updateDialogs('saveIsOpen', false);
                  }}
                >
                  Save
                </Button>
                <Button
                  variant={VARIANTS.contained}
                  onClick={() => {
                    saveChangesSetGroupDPSKs();
                    updateDialogs('saveIsOpen', false);
                  }}
                >
                  Save and Set Group DPSKs
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <>
            {smartzoneId && (
            <>
              <Button
                variant={VARIANTS.outlined}
                color={BUTTON_COLORS.error}
                onClick={() => updateDialogs('promptIsOpen', true)}
              >
                Delete Zone
              </Button>
              <UserPrompt
                isOpen={promptIsOpen}
                title="Delete Zone Configuration?"
                message="Are you sure you wish to delete this building's configured zone? All affiliated dpsk records in the database will also be permanently removed. If these need to be deleted elsewhere this must be handled from the vsz UI."
                denyCallback={() => updateDialogs('promptIsOpen', false)}
                acceptBtnText="Delete"
                acceptCallback={deleteZoneConfig}
                primaryBtnColor={BUTTON_COLORS.error}
              />
            </>
            )}
            <Button
              variant={VARIANTS.contained}
              disabled={
              !id
                  || dirty
                  || isLoading
                  || vszError
            }
              onClick={setBuildingGroupDPSK}
            >
              Set Group DPSKs
            </Button>
          </>
        )}
      </Grid>
    </>
  );
}
